import { Navigate, RouteObject } from "react-router-dom";
import { getStage } from "src/context";
import { AdminSideNavLayout } from "src/routes/admin/components/AdminSideNavLayout";
import { CostCodesPage } from "src/routes/admin/costCodes/CostCodesPage";
import { FeatureFlagsPage } from "src/routes/admin/featureFlags/FeatureFlagsPage";
import { FeatureTypesPage } from "src/routes/admin/featureTypes/FeatureTypesPage";
import { InfrastructurePage } from "src/routes/admin/infrastructure/InfrastructurePage";
import { ItemsPage } from "src/routes/admin/items/ItemsPage";
import { LocationsPage } from "src/routes/admin/locations/LocationsPage";
import { AdminProjects } from "src/routes/admin/projects/AdminProjects";
import { UserGroupsPage } from "src/routes/admin/userGroups/UserGroups";
import { UsersPage } from "src/routes/admin/users/UsersPage";
import { NotFound } from "src/routes/NotFound";
import { TradeCategories } from "src/routes/trade-categories/TradeCategories";
import { AppShell } from "src/routing/components/AppShell";
import { BeamNavbarLayout } from "src/routing/components/layouts/BeamNavbarLayout";
import * as adminPaths from "src/routing/domains/adminPaths";

/**
 * `/admin/*` route tree.
 *
 * Role: data router domain route.
 *
 * Default stack: `AppShell` → `BeamNavbarLayout` → `AdminSideNavLayout` → page (`PageHeaderLayout`).
 * Trade Categories is a bookmark-only sibling under `BeamNavbarLayout` (no Admin side nav / not in the rail).
 * First domain to use Beam's `SideNavLayout` (via `BeamSideNavLayout` / `AdminSideNavLayout`).
 * Do not nest `ScrollableParentLayout` — that layout is legacy-only (see `src/routing/README.md`).
 *
 * Path constants live in `adminPaths.ts` (imported by pages / stories) to avoid a cycle
 * through this file's page `Component` imports.
 * Legacy `Routes.tsx` must not register these paths.
 */
const adminDomainRoute: RouteObject = {
  path: adminPaths.adminRoute.path,
  Component: AppShell,
  children: [
    {
      Component: BeamNavbarLayout,
      children: [
        // Sibling of AdminSideNavLayout so `/admin/tradeCategories` is not caught by the Admin `*` NotFound.
        { path: adminPaths.adminTradeCategoriesRoute.path, Component: TradeCategories },
        {
          Component: AdminSideNavLayout,
          children: [
            { index: true, element: <Navigate to={adminPaths.adminProjectsRoute.url()} replace /> },
            { path: adminPaths.adminProjectsRoute.path, Component: AdminProjects },
            { path: adminPaths.adminFeatureFlagsRoute.path, Component: FeatureFlagsPage },
            { path: adminPaths.adminUsersRoute.path, Component: UsersPage },
            { path: adminPaths.adminUserGroupsRoute.path, Component: UserGroupsPage },
            { path: adminPaths.adminItemsRoute.path, Component: ItemsPage },
            { path: adminPaths.adminCostCodesRoute.path, Component: CostCodesPage },
            { path: adminPaths.adminLocationsRoute.path, Component: LocationsPage },
            { path: adminPaths.adminFeatureTypesRoute.path, Component: FeatureTypesPage },
            ...(getStage() === "qa"
              ? [{ path: adminPaths.adminInfrastructureRoute.path, Component: InfrastructurePage }]
              : []),
            { path: "*", Component: NotFound },
          ],
        },
      ],
    },
  ],
};

/**
 * Migrated `/admin/*` route trees registered in `Router.tsx`.
 */
export const adminRoutes = [adminDomainRoute];
